import { serverCacheService } from '@/server/utils/serverCacheService';
import { debugLog } from '@/utils/debugLogger';

// Global variable to access AudioContext functions
let audioContextFunctions: {
  isAudioPlaying: () => boolean;
  getActiveAudioIds: () => string[];
  isAudioProtectionMode: () => boolean;
} | null = null;

// Function to set the AudioContext functions
export function setAudioContextFunctions(functions: typeof audioContextFunctions) {
  audioContextFunctions = functions;
}

// Queue for pending invalidations during audio protection mode
const pendingInvalidations: (() => void)[] = [];

export interface AudioCacheEntry {
  audioData: string; // base64 encoded audio data
  timestamp: number;
  contentHash: string;
  contentType: 'blog' | 'quotes' | 'predictions';
}

// Cache TTL configurations (in milliseconds)
export const AUDIO_CACHE_TTL = {
  blog: 365 * 24 * 60 * 60 * 1000, // 365 days (almost permanent)
  quotes: 30 * 1000, // 30 seconds (sync with spot prices)
  predictions: 24 * 60 * 60 * 1000, // 24 hours (until new predictions)
};

function isAudioCurrentlyPlaying(contentType: string, contentId: string): boolean {
  if (!audioContextFunctions) {
    return false;
  }
  
  const activeIds = audioContextFunctions.getActiveAudioIds();
  const targetId = `${contentType}:${contentId}`;
  
  return activeIds.some(id => id.includes(targetId));
}

export const audioCacheService = {
  generateCacheKey(contentType: string, contentId: string, contentHash: string): string {
    return `audio:${contentType}:${contentId}:${contentHash}`;
  },

  generateContentHash(text: string): string {
    // Simple hash function for content fingerprinting
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
      const char = text.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  },

  get(contentType: string, contentId: string, text: string): AudioCacheEntry | null {
    const contentHash = this.generateContentHash(text);
    const cacheKey = this.generateCacheKey(contentType, contentId, contentHash);
    
    const entry = serverCacheService.get<AudioCacheEntry>(cacheKey);
    
    if (!entry) {
      return null;
    }

    // Verify content hasn't changed
    if (entry.contentHash !== contentHash) {
      serverCacheService.clear(); // Clear stale entry
      return null;
    }

    // Check if cache is still valid based on content type
    const ttl = AUDIO_CACHE_TTL[contentType as keyof typeof AUDIO_CACHE_TTL] || AUDIO_CACHE_TTL.quotes;
    const isExpired = Date.now() - entry.timestamp > ttl;
    
    if (isExpired) {
      this.invalidate(contentType, contentId);
      return null;
    }

    return entry;
  },

  set(contentType: string, contentId: string, text: string, audioData: string): void {
    const contentHash = this.generateContentHash(text);
    const cacheKey = this.generateCacheKey(contentType, contentId, contentHash);
    
    const entry: AudioCacheEntry = {
      audioData,
      timestamp: Date.now(),
      contentHash,
      contentType: contentType as AudioCacheEntry['contentType'],
    };

    serverCacheService.set(cacheKey, entry);
  },

  invalidate(contentType?: string, contentId?: string, respectPlayingAudio: boolean = true): void {
    // Check global audio protection mode first
    if (respectPlayingAudio && audioContextFunctions?.isAudioProtectionMode()) {
      const invalidationFn = () => this.invalidate(contentType, contentId, false);
      pendingInvalidations.push(invalidationFn);
      debugLog('audio', `Queued invalidation due to audio protection mode: ${contentType}:${contentId || 'ALL'}`, undefined, 'Audio Cache');
      return;
    }

    if (!contentType) {
      // Clear all audio cache (only if no audio is playing)
      if (respectPlayingAudio && audioContextFunctions?.isAudioPlaying()) {
        debugLog('audio', 'Skipping full cache clear due to active audio playback', undefined, 'Audio Cache');
        return;
      }
      serverCacheService.clear();
      return;
    }

    if (!contentId) {
      // Clear all cache for content type
      const pattern = new RegExp(`^audio:${contentType}:`);
      this.invalidatePattern(pattern, respectPlayingAudio);
      return;
    }

    // Check if the specific audio is currently playing
    if (respectPlayingAudio && isAudioCurrentlyPlaying(contentType, contentId)) {
      debugLog('audio', `Skipping invalidation for playing audio: ${contentType}:${contentId}`, undefined, 'Audio Cache');
      return;
    }

    // Clear specific content cache
    const pattern = new RegExp(`^audio:${contentType}:${contentId}:`);
    this.invalidatePattern(pattern, respectPlayingAudio);
  },

  invalidateQuotes(): void {
    debugLog('audio', 'Attempting to invalidate quotes cache due to price update', {
      isAudioPlaying: audioContextFunctions?.isAudioPlaying(),
      isProtectionMode: audioContextFunctions?.isAudioProtectionMode(),
      activeAudioIds: audioContextFunctions?.getActiveAudioIds()
    }, 'Audio Cache');
    
    this.invalidate('quotes');
  },

  invalidatePredictions(predictionId?: string): void {
    debugLog('audio', 'Invalidating predictions cache', undefined, 'Audio Cache');
    if (predictionId) {
      this.invalidate('predictions', predictionId);
    } else {
      this.invalidate('predictions');
    }
  },

  invalidateBlogPost(slug: string): void {
    debugLog('audio', `Invalidating blog post cache for slug: ${slug}`, undefined, 'Audio Cache');
    this.invalidate('blog', slug);
  },

  invalidatePattern(pattern: RegExp, respectPlayingAudio: boolean = true): void {
    // Check global audio protection mode first
    if (respectPlayingAudio && audioContextFunctions?.isAudioProtectionMode()) {
      const invalidationFn = () => this.invalidatePattern(pattern, false);
      pendingInvalidations.push(invalidationFn);
      debugLog('audio', `Queued pattern invalidation due to audio protection mode: ${pattern}`, undefined, 'Audio Cache');
      return;
    }

    if (respectPlayingAudio && audioContextFunctions?.isAudioPlaying()) {
      debugLog('audio', `Skipping cache invalidation due to active audio playback: ${pattern}`, undefined, 'Audio Cache');
      return;
    }
    
    const removedCount = serverCacheService.removeByPattern(pattern);
    debugLog('audio', `Pattern invalidation removed ${removedCount} entries: ${pattern}`, undefined, 'Audio Cache');
  },

  // Execute all pending invalidations (called when audio protection is disabled)
  executePendingInvalidations(): void {
    if (pendingInvalidations.length > 0) {
      debugLog('audio', `Executing ${pendingInvalidations.length} pending invalidations`, undefined, 'Audio Cache');
      pendingInvalidations.forEach(fn => fn());
      pendingInvalidations.length = 0; // Clear the array
    }
  },

  getStats(): { totalEntries: number; cachesByType: Record<string, number> } {
    const stats = serverCacheService.getStats();
    const cachesByType: Record<string, number> = {
      blog: 0,
      quotes: 0,
      predictions: 0,
    };
    
    stats.keys.forEach(key => {
      if (key.startsWith('audio:blog:')) cachesByType.blog++;
      else if (key.startsWith('audio:quotes:')) cachesByType.quotes++;
      else if (key.startsWith('audio:predictions:')) cachesByType.predictions++;
    });
    
    return {
      totalEntries: stats.totalEntries,
      cachesByType,
    };
  },
};